/**
 * Client-error reporting — the browser half of the unified logging system
 * (genus-php/docs/logging.md "Client errors").
 *
 * initClientLog() attaches the global window 'error' + 'unhandledrejection'
 * listeners ONCE per page (g.init calls it); every uncaught error is POSTed
 * to /frm/log/client — fire and forget, no retry — where the API appends it
 * to the unified log with origin 'client'. A site opts out with the config
 * key site.clientLog => false (rides g.init as settings.clientLog; checked
 * at REPORT time, so init order never matters).
 *
 * Discipline (the endpoint is public — the client keeps the noise down):
 *   - dedup: the same message+source+line+col never reports twice per page;
 *   - cap: at most MAX_REPORTS reports per page load;
 *   - g.fetch never throws, so the reporter cannot loop on itself — and
 *     report() swallows everything anyway (a reporter must never BE the
 *     error).
 *
 * g.logError(err, context?) reports deliberately — a caught error worth a
 * server-side trace. Same pipeline, same caps. Accepts an Error (message +
 * stack travel) or any value (stringified).
 */
import { settings } from './core.js';
import { apiFetch } from './fetch.js';

const MAX_REPORTS = 10;
const seen = new Set();
let sent = 0;
let attached = false;

function report(payload) {
	if (settings.clientLog === false) return;
	if (sent >= MAX_REPORTS) return;
	const key = `${payload.message}|${payload.source ?? ''}|${payload.line ?? ''}|${payload.col ?? ''}`;
	if (seen.has(key)) return;
	seen.add(key);
	sent += 1;
	try {
		apiFetch('/frm/log/client', { ...payload, url: window.location.href }, { progress: false, unguarded: true });
	} catch { /* never let the reporter become the error */ }
}

export function logError(err, context) {
	const isError = err instanceof Error;
	const message = isError ? err.message : String(err ?? '');
	report({
		level: 'error',
		message: message || 'Unknown error',
		...(isError && err.stack ? { stack: String(err.stack) } : {}),
		...(context && typeof context === 'object' ? { context } : {}),
	});
}

export function initClientLog() {
	if (attached || typeof window === 'undefined') return;
	attached = true;
	// no capture: window-targeted error events = JS runtime errors only
	// (resource-load failures don't bubble and stay out by design)
	window.addEventListener('error', (event) => {
		report({
			level: 'error',
			message: event.message || String(event.error?.message ?? 'Unknown script error'),
			...(event.filename ? { source: event.filename } : {}),
			...(event.lineno ? { line: event.lineno } : {}),
			...(event.colno ? { col: event.colno } : {}),
			...(event.error?.stack ? { stack: String(event.error.stack) } : {}),
		});
	});
	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		const isError = reason instanceof Error;
		report({
			level: 'error',
			message: isError ? reason.message : String(reason ?? 'Unhandled rejection'),
			...(isError && reason.stack ? { stack: String(reason.stack) } : {}),
			context: { type: 'unhandledrejection' },
		});
	});
}
